/**
 * The bounds narration is allowed to fail inside (round 4, in-model defect; reshaped round 5).
 *
 * Same discipline as every other file in this folder (D-010/D-023): **every value is invented and
 * flagged**, no source document names any of them, and none of them scores, gates or teaches
 * anything. What they decide is how long the platform waits for a speech engine before concluding
 * it has silently dropped a line.
 *
 * **Why a watchdog exists at all.** `browser-tts-narration-service` plays one line at a time so a
 * five-year-old never hears two voices at once, and each line settles when the browser fires `onend`
 * or `onerror`. Measured in round 4: a voiceless or headless Chromium accepts `speak()` and then
 * fires *neither*. The line never settles, the queue never advances, and every sentence after it —
 * the next round's instruction, the repeat a child asks for with R, every hint — is silently lost
 * for the rest of the sitting. Narration is how a pre-reader plays, so that is not a cosmetic bug.
 *
 * **Why it is not a duration (S5-6).** Round 4 measured the whole utterance against a
 * length-derived deadline from the moment `speak()` was called, which asks the wrong question: an
 * engine that is speaking slowly is not an engine that has dropped the line. The shipped
 * 81-character Portal Pick instruction got 13.8 s, and a live 60-words-a-minute voice — the kind a
 * family turns on for a young child, or an accessibility profile — needs about 17 s of genuine
 * speech, so `cancel()` cut a child's instruction off three seconds early. **A live utterance must
 * never be cancelled**, so the bound is armed on *engine activity* instead: did it begin, and is it
 * still going. Both directions are pinned by test.
 *
 * The runtime is already insulated (it fires narration and forgets it), so a hung engine cannot stall
 * a child's turn — that is proved separately. This is about the narration itself surviving.
 */
export const NARRATION_BOUNDS = {
  /**
   * How long the engine has to **begin** a line before it is presumed to have dropped it
   * (invented, flagged).
   *
   * The one unconditional give-up, and the only one that can be short, because it cannot possibly
   * cut a live utterance: an utterance that has not fired `onstart` is by definition one nobody is
   * saying. Round 4's floor was three seconds for exactly this reason — "longer than any plausible
   * start-up delay" — and that is the job it does here, now on its own rather than added to a
   * length estimate. Deliberately **not** divided by the speaking rate: how long a browser takes to
   * pick a voice and get going has nothing to do with how fast it will then talk.
   */
  utteranceStartTimeoutMs: 3_000,

  /**
   * How long a line of speech may take **per character**, once the engine has actually started
   * (invented, flagged).
   *
   * Derived from the *slowest* delivery anybody plausibly hears, not from the ordinary one — the
   * inversion S5-6 turned on. English averages ~5.8 characters a word including the space, so a
   * deliberately slow 60-words-a-minute voice is ~5.8 characters a second, or ~172 ms a character.
   * Doubled to 350, because the failure directions are not symmetric and never have been: too long
   * means one dropped line stays dropped a little longer, and too short means cutting a
   * five-year-old's instruction off mid-sentence. Only the second is a harm.
   *
   * It is measured from the moment the engine **started**, not from `speak()`, so start-up latency
   * no longer eats into the speaking budget; it is re-armed against the remaining text at every word
   * boundary the engine reports; and it is divided by the utterance's own `rate`, so a profile that
   * deliberately speaks slowly gets proportionally longer still. Engines that never fire boundary
   * events lose only the re-arming — the bound stands on its own, which is why it is derived from
   * the whole line rather than from the gap between two words.
   */
  utteranceMsPerCharacter: 350,

  /**
   * Slack added to the post-start bound, whatever the line's length (invented, flagged).
   *
   * A per-character estimate says nothing about the pause a voice takes at a full stop, the breath
   * before a question, or the moment between `onstart` and the first sound. Three seconds of it,
   * the same order as the start-up bound and for the same reason: this must only ever fire for a
   * line that has stopped being said.
   */
  utteranceProgressGraceMs: 3_000,

  /**
   * How long a **recorded** line may take per character before it is presumed lost (invented,
   * flagged; Slice A″).
   *
   * The same harm as the TTS watchdog above, on the path that replaced it: a media file that will
   * not load, or an element that never fires `ended`, would otherwise hold the queue — and every
   * sentence behind it — for the rest of the sitting.
   *
   * **Derivation:** a recording is not a live voice, so its length is known by measurement rather
   * than guessed. The shipped bake — 256 lines, 13 366 characters, 833 s of audio — runs at a mean
   * of **62.3 ms a character**, and its slowest single line at **90.5 ms a character** (short lines
   * carry a file's fixed head and tail; `tools/audio/bake-narration.mjs` prints both figures on
   * every run). 150 is ~2.4× the mean and ~1.7× the slowest line ever produced, which keeps the
   * asymmetry the TTS bound is built on: too long means one lost line stays lost a moment longer,
   * too short means cutting a five-year-old off mid-sentence, and only the second is a harm.
   *
   * Unlike the TTS figure this is **not** divided by a speaking rate: a recording plays at the
   * speed it was baked at, and nothing at runtime changes it.
   */
  bakedLineMsPerCharacter: 150,

  /**
   * Slack added to a recording's bound, whatever the line's length (invented, flagged; Slice A″).
   *
   * Covers everything that is not speaking: the element being created, the file being fetched from
   * the local origin, the decoder starting, and the silence a synthesised file carries at each end.
   * Three seconds, the same order as the two TTS allowances and for the same reason — this must
   * only ever fire for a line that is not being heard.
   */
  bakedLineStartGraceMs: 3_000,
} as const;

export type NarrationBounds = typeof NARRATION_BOUNDS;
